"use client"

import { useState } from "react";
import { subscribeToNewsletter } from "@/lib/newsletter-action";

export default function AuthorNewsletter({ authorName, authorId }: { authorName: string, authorId: string }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    
    // Pass the author name as the source and the ID for reference
    const result = await subscribeToNewsletter(email, `Author Briefing: ${authorName}`, authorId);
    
    if (result.success) {
        setStatus("success");
        setEmail("");
    } else if (result.message === "ALREADY_SUBSCRIBED") {
        setStatus("error");
        setMessage("You have already subscribed to this author's briefing.");
    } else {
        setStatus("error");
        setMessage(result.message);
    }
  };

  return (
    <div className="bg-zinc-950 text-white p-8 md:p-12 border-y border-brand/20 relative overflow-hidden my-16">
      <div className="absolute inset-0 bg-brand/5 pointer-events-none" />
      
      <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center text-center">
        <span className="text-brand font-bold uppercase tracking-[0.4em] text-[10px] mb-6 block">Exclusive Editorial Access</span>
        <h2 className="font-serif text-3xl md:text-5xl font-bold mb-6">Subscribe to {authorName}&rsquo;s Briefing.</h2>
        <p className="text-zinc-400 font-serif text-lg md:text-xl mb-12 leading-relaxed max-w-xl">
          Get notified specifically when {authorName} publishes deep-dive reports and breaking analysis. No noise, just the shift.
        </p>

        {status === "success" ? (
          <div className="bg-brand/10 border border-brand/50 p-8 rounded-sm animate-in fade-in zoom-in duration-500 w-full max-w-lg">
            <h3 className="text-xl font-bold mb-2">You&rsquo;re Subscribed.</h3>
            <p className="text-sm text-zinc-300">You will receive the next briefing from {authorName} directly in your inbox.</p>
          </div>
        ) : (
          <div className="w-full max-w-lg">
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <input 
                type="email" 
                required
                placeholder="Enter your professional email"
                className="w-full bg-white/5 border border-white/10 px-6 py-5 rounded-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-all text-white placeholder:text-zinc-600 font-serif text-lg"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button 
                type="submit"
                disabled={status === "loading"}
                className="w-full bg-brand hover:bg-brand-dark px-10 py-5 font-bold uppercase tracking-[0.3em] text-xs transition-all disabled:opacity-50 shadow-lg shadow-brand/20 hover:translate-y-[-2px]"
              >
                {status === "loading" ? "Processing..." : "Join Author's Circle"}
              </button>
            </form>
            {status === "error" && (
                <p className="text-red-500 text-[10px] font-bold uppercase tracking-widest mt-4 animate-shake">{message}</p>
            )}
          </div>
        )}
        
        <div className="flex items-center gap-4 mt-12 opacity-30">
            <div className="h-px w-8 bg-zinc-500" />
            <p className="text-[9px] text-zinc-500 uppercase tracking-widest whitespace-nowrap">
                Guaranteed Privacy • No Secondary Distribution
            </p>
            <div className="h-px w-8 bg-zinc-500" />
        </div>
      </div>
    </div>
  );
}
